import React from 'react';
import { Globe, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const About = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <Globe className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Hakkımızda</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              RUYAN Bilişim
            </h1>

            <p className="text-xl text-gray-300 mb-6 leading-relaxed">
              RUYAN Bilişim, markaların dijitalde daha akıllı, daha hızlı ve ölçülebilir büyümesini sağlamak için kurulmuş bir teknoloji ve dijital strateji ajansıdır.
            </p>

            <p className="text-lg text-gray-400 leading-relaxed">
              Biz yalnızca tasarım yapan ya da reklam yöneten bir ajans değiliz.<br />
              Veriyi, yapay zekâyı ve kullanıcı davranışlarını merkeze alarak gerçek sonuç üreten dijital sistemler kurarız.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Ne Yapıyoruz?</span>
          </h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              'Yapay zekâ destekli SEO ve içerik mimarileri',
              'Kullanıcı deneyimi (UX) ve dönüşüm optimizasyonu',
              'Performans odaklı Google & Meta Ads yönetimi',
              'Web & yazılım çözümleri',
              'Ölçeklenebilir dijital büyüme altyapıları'
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 hover:border-cyan-500/50 transition-all"
              >
                <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-300 text-lg">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Nasıl Çalışıyoruz?</span>
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 md:p-12">
              <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
                <p className="text-white font-semibold text-xl">
                  RUYAN'da her proje önce analizle başlar.
                </p>
                <p>
                  Varsayımlarla değil, veriyle ilerleriz.
                </p>
                <div className="space-y-3 pl-6 border-l-4 border-cyan-500">
                  <p className="text-white">Sitenizde kullanıcılar nerede kayboluyor?</p>
                  <p className="text-white">Hangi sayfa para kazandırıyor, hangisi kaybettiriyor?</p>
                  <p className="text-white">Bütçe nereye gidiyor, nerede boşa harcanıyor?</p>
                </div>
                <p className="text-cyan-400 font-semibold">
                  Bu soruların net cevaplarını üretmeden hiçbir çözüm sunmayız.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Neye İnanıyoruz?</span>
          </h2>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
            {[
              'Yapay zekâ bir "sihir" değil, doğru kullanıldığında rekabet avantajıdır',
              'Dijital büyüme rastlantı değil, sistem işidir',
              'Her markanın tek bir doğru stratejisi yoktur',
              'Bu yüzden hazır paketler yerine, ihtiyaca özel çözümler üretiriz'
            ].map((belief, index) => (
              <div 
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/50 transition-all"
              >
                <p className="text-gray-300 leading-relaxed">{belief}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work For */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Kimler İçin?</span>
          </h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              'Dijitalden gerçek gelir elde etmek isteyen markalar',
              'Trafiği var ama dönüşümü düşük olan işletmeler',
              '"Reklam veriyoruz ama sonuç alamıyoruz" diyenler',
              'Ölçeklenmek isteyen girişimler ve KOBİ\'ler'
            ].map((client, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/50 transition-all"
              >
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-300 text-lg">{client}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Kurucu Hikâyesi</span>
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 md:p-12">
              <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
                <p>
                  RUYAN Bilişim'in hikâyesi, 2009 yılında ilk web sitesini yayınlayan bir merakla başladı.
                </p>
                <p>
                  O günden bu yana değişen teknolojiye değil, değişmeyen kullanıcı davranışına odaklandı.
                </p>
                <p>
                  SEO, performans, kullanıcı deneyimi ve yapay zekâ; RUYAN'da ayrı ayrı hizmetler değil, 
                  tek bir büyüme sisteminin parçalarıdır.
                </p>
                <div className="bg-slate-900/50 border-l-4 border-cyan-500 p-6 rounded-r-xl my-8">
                  <p className="text-cyan-400 font-semibold text-xl italic">
                    "Ölçülemeyen hiçbir şey optimize edilemez."
                  </p>
                  <p className="text-sm text-gray-500 mt-3">
                    Temel ilke nettir.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12 text-center">
            <p className="text-2xl md:text-3xl text-gray-300 leading-relaxed mb-6">
              RUYAN Bilişim, dijital dünyada ne yaptığını bilen,<br />
              neyi neden yaptığını ölçebilen markalar için çalışır.
            </p>
            <p className="text-xl text-cyan-400 font-semibold">
              Bizim işimiz daha fazla laf değil,<br />
              daha iyi sonuç üretmek.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Birlikte Çalışmaya
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Hazır mısınız?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Dijital büyüme yolculuğunuzda yanınızda olmaktan mutluluk duyarız.
          </p>
          <a
            href="/iletisim"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105"
          >
            <Zap className="w-5 h-5" />
            İletişime Geçin
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
